import { cloneDeep, get } from 'lodash';
import { IBookingStateEvent } from '../../../../../data/entities/booking-state';
import { IEventV2, IEventV3 } from '../../../../../data/entities/event';
import { IBookingBreakdown } from '../../../../../data/entities/event/booking';
import { Channel } from '../../../../../entities/contracts/constants';
import { sortByCreatedAt } from '../../../../components/booking-list/helpers';

export interface IFXRateInfo {
	from: string;
	to: string;
	fxRate: number;
}

export interface IFXRates { [date: string]: IFXRateInfo; }

export const calculateDiscrepancyRate = (bookingDiscrepancy: number, total: number) => {
	// the discrepancy is between the bookingDiscrepancy and the value we (TN) believe the booking
	// should come to (TBV minus the discrepancy)
	return total > 0 ? bookingDiscrepancy / (total - bookingDiscrepancy) : 0;
};

export const calculateOwnerTotal = (ownerRent: number, extras: number) => {
	return ownerRent + extras;
};

const RestrictionInfo = {
	'early arrival': 'Early arrival',
	'smoking': 'Smoking',
	'pets': 'Pets',
	'wheelchair': 'Wheelchair',
	'group bookings': 'Group bookings',
	'children allowed': 'Children allowed',
	'infants allowed': 'Infants allowed',
	'late departure': 'Late departure',
	'extra occupancy': 'Extra occupancy',
	'cleaning fee': 'Cleaning fee',
	'tourist tax': 'Tourist tax',
};

export const zeroIsTruthy = (value: number) => {
	return !!value || value === 0;
};

export const displayVersionOfRestictionName = (restriction: string) => RestrictionInfo[restriction] || 'Extra fee';

export const financesHaveDiscrepancy = (breakdown: IBookingBreakdown, total: number | undefined) => {
	const safeTotal = total || 0;
	const acceptableDiscrepancyPercent = 10;
	const discrepancyPercent = calculateDiscrepancyRate(get(breakdown, 'travelnest.booking_discrepancy'), safeTotal) * 100;
	return Math.abs(discrepancyPercent) > acceptableDiscrepancyPercent;
};

export const breakdownHasSucceeded = (breakdown: IBookingBreakdown) => {
	return zeroIsTruthy(get(breakdown, 'travelnest.commission_rate')) &&
		zeroIsTruthy(get(breakdown, 'owner.rent_value'));
};

export const calculateCSBookingFee = (total: number, discountedOwnerRent: number, extras: number[]) => {
	if (typeof total !== 'number')
		return;
	if (typeof discountedOwnerRent !== 'number')
		return;
	const totalExtras = extras.reduce((sum, curr) => sum + curr, 0);
	return total - (totalExtras + discountedOwnerRent);
};

export const isFirstBooking = (bookings: IEventV3[], bookingId: string) => {
	const first = bookings && cloneDeep(bookings).filter((b) => b.status !== 'failed').sort(sortByCreatedAt(true))[0];
	return get(first, 'id') === bookingId;
};

export const VCC_STATUS_NOTE_TYPE = 'vcc status';

/**
 * Look for a VCC status note in a given event to determine if it is a vcc booking.
 * @param booking IEventV2 - The event to check
 *
 * @returns isVCC {boolean} - True if there is a note indicating the booking is a VCC booking
 */
export const isVCCBooking = (booking: IEventV2): boolean => {
	// Sort latest created descending to get most recent VCC note
	const vccStatusNote = booking.notes.sort((a, b) => b.created_at < a.created_at ? -1 : 1).find((n) => n.note_type === 'vcc status');
	if (vccStatusNote) {
		return vccStatusNote.content.startsWith('true');
	} else {
		return false;
	}
};

export const isBookingConfirmed = (event: IEventV3 | IEventV2, stateEvents: IBookingStateEvent[]): boolean => {
	const { notes } = event;

	const stateResult = !!stateEvents && stateEvents.some((state) => (
		state.state_event_type && state.state_event_type.includes('BOOKING_MANUALLY_CONFIRMED')
		|| state.state_event_type.includes('VERIFICATION_PASSED'))
	);
	const notesResult = !!notes && notes.some((note) => (
		note.content.includes('manual_booking_confirmation') || note.content.includes('owner_booking_confirmation')
	));
	return stateResult || notesResult;
};

export const getCoreChannel = (source: string): string => {
	const result = source.split(':');
	return result[0] === Channel.RENTALS_UNITED ? result[1] : result[0];
};
